import { Component, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { DataService } from 'src/app/services/data.service';
import { PatientService } from 'src/app/services/patient.service';
import { PayorService } from 'src/app/services/payor.service';
import { SnackService } from 'src/app/services/snack.service';

@Component({
  selector: 'app-insurance-dialog',
  templateUrl: './insurance-dialog.component.html',
  styleUrls: ['./insurance-dialog.component.scss'],
})
export class InsuranceDialogComponent implements OnInit {
  coverageForm: UntypedFormGroup;
  payors$: Observable<any[]>;
  currentPayorIndex = 0;

  constructor(
    private patientService: PatientService,
    private db: AngularFirestore,
    private payorService: PayorService,
    public data: DataService,
    private fs: UntypedFormBuilder,
    public dialogRef: MatDialogRef<InsuranceDialogComponent>,
    private snackService: SnackService
  ) {}

  ngOnInit(): void {
    this.coverageForm = this.fs.group({
      payorsGroup: this.fs.array([this.createPayor()]),
    });
    this.setupPayorListener(this.payorsGroup.at(0) as UntypedFormGroup);
  }

  createPayor(): UntypedFormGroup {
    return this.fs.group({
      enableCheckbox: [false],
      payor: ['', Validators.required],
      member_id: ['', Validators.required],
      groupId: [''],
      payorType: ['', Validators.required],
      payorIds: [[]],
    });
  }

  setupPayorListener(control: UntypedFormGroup): void {
    control
      .get('payor')
      .valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        filter((value) => typeof value === 'string' && value.length > 2),
        tap(() => control.get('payorIds').setValue([])),
        distinctUntilChanged(),
        takeUntil(this.dialogRef.afterClosed()),
        switchMap((value) => this.payorService.getPayorsByName(value).pipe(
          take(1),
          catchError(() => {
            this.snackService.genericSnackBar('Error searching payors', ['error-snackbar'], 5000);
            return of([]);
          })
        ))
      )
      .subscribe((payors) => {
        this.payors$ = of(payors);
      });

    control
      .get('payor')
      .valueChanges.pipe(
        filter((value) => typeof value === 'object'),
        takeUntil(this.dialogRef.afterClosed())
      )
      .subscribe((value) => {
        control.get('payorIds').setValue(value?.payer_ids || []);
      });
  }

  displayFn(content: any): string {
    return content ? content?.payor_name : '';
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  async onSaveCoverage(): Promise<void> {
    try {
      const userRef = this.db.collection('users').doc(this.patientService.currentPatientServiceID).collection('my_health_coverage');

      const payors = this.coverageForm.get('payorsGroup').value;

      const coverageData = payors.map((payor: any) => {
        const base = {
          member_id: payor.member_id,
          group_id: payor.groupId || null,
          type: payor.payorType,
        };
        if (payor.enableCheckbox) {
          return {
            ...base,
            payor: {
              payor_name: payor.payor,
              payer_ids: [payor.member_id],
            },
          };
        } else {
          return {
            ...base,
            payor: payor.payor,
          };
        }
      });

      const docs = (await userRef.get().toPromise()).docs.map((doc) => doc.data());
      for (const coverage of coverageData) {
        if (docs.find((doc) => doc.payor.payor_name === coverage.payor.payor_name && doc.member_id === coverage.member_id)) {
          this.snackService.genericSnackBar('Coverage Already Exists', ['error-snackbar'], 5000);
          return;
        }
      }

      for (const coverage of coverageData) {
        await userRef.add(coverage);
      }
      this.snackService.genericSnackBar('Coverage Saved', ['success-snackbar'], 5000);
      this.dialogRef.close();
    } catch (error) {
      console.error(error);
      this.snackService.genericSnackBar('Error saving coverage', ['error-snackbar'], 5000);
    }
  }

  check(index: number): void {
    const payorControl = this.payorsGroup.at(index) as UntypedFormGroup;
    payorControl.get('payor').setValue('');
    payorControl.get('member_id').setValue('');
    payorControl.get('groupId').setValue('');
    payorControl.get('payorIds').setValue([]);
  }

  addPayor(): void {
    const newPayor = this.createPayor();
    this.payorsGroup.push(newPayor);
    this.setupPayorListener(newPayor);
    this.currentPayorIndex = this.payorsGroup.length - 1;
  }

  deletePayor(index: number): void {
    this.payorsGroup.removeAt(index);
    if (this.currentPayorIndex >= this.payorsGroup.length) {
      this.currentPayorIndex = Math.max(0, this.payorsGroup.length - 1);
    }
  }

  prevPayor(): void {
    if (this.currentPayorIndex > 0) {
      this.currentPayorIndex--;
    }
  }

  nextPayor(): void {
    if (this.currentPayorIndex < this.payorsGroup.length - 1) {
      this.currentPayorIndex++;
    }
  }

  get payorsGroup(): UntypedFormArray {
    return this.coverageForm.get('payorsGroup') as UntypedFormArray;
  }

  get hasInvalidPayor(): boolean {
    return this.payorsGroup.controls.some((control) => control.invalid);
  }
}
